import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchUserProfile } from '../../store/userSlice';
import {
 Bot,
 Globe,
 Cpu,
 RefreshCw,
 Loader2,
 AlertCircle,
 Activity,
 Shield,
 CheckCircle,
 XCircle,
 Clock,
 User,
 Calendar,
 Crown,
 X,
 Info
} from 'lucide-react';
import {
 getSoraCarList,
 getGptCarList,
 getCCarList,
 getClaudeV2CarList,
 getGrokCarList,
 getGptAPICarList,
 getGeminiCarList
} from '../../api/carApi';
import { getAuthBridgeUrl } from '../../config/aiFleetConfig';
import { fleetFilterTags, getTagColorStyles, defaultFilterConfig } from '../../config/nodeFilterTags';
import fleetCardsConfig from '../../config/fleetCardsConfig.json';

// 车队 type → /web_info 接口下发的 type key（前端 type 命名与后端不一致时在此对齐）
const FLEET_TYPE_TO_WEB_INFO_KEY = {
 'gpt-backup': 'gpt',
 'claude-v2': 'claude',
};

// 把前端车队 type 解析为接口 type key
// 规则：(1) 无车队 → gpt（默认） (2) 命中映射表 → 映射 (3) 含 'gpt' 子串 → gpt (4) 否则原样返回
const resolveWebInfoKey = (fleetType) => {
 if (!fleetType) return 'gpt';
 if (FLEET_TYPE_TO_WEB_INFO_KEY[fleetType]) return FLEET_TYPE_TO_WEB_INFO_KEY[fleetType];
 if (String(fleetType).toLowerCase().includes('gpt')) return 'gpt';
 return fleetType;
};

export default function AIFleetGrid({ activeTab, tabs, defaultSelectedFleetId, user, navigate, isLoggedIn, onLoginRequired }) {
 const { t, i18n } = useTranslation();
 const dispatch = useDispatch();
 const [forceUpdate, setForceUpdate] = useState(0);
 const [fleets, setFleets] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState('');
 const [selectedFleet, setSelectedFleet] = useState(null);
 const [nodeData, setNodeData] = useState({}); // 存储节点数据
 const [loadingNodes, setLoadingNodes] = useState(false); // 节点加载状态
 const [webInfoTips, setWebInfoTips] = useState({}); // 后端按车队类型下发的提醒文案
 
 // 筛选标签状态
 const [activeFilters, setActiveFilters] = useState(defaultFilterConfig.defaultSelected);
 const [filteredNodes, setFilteredNodes] = useState([]); // 筛选后的节点
 const cleanupTimeoutRef = React.useRef(null); // 添加timeout引用
 const abortControllerRef = React.useRef(null); // 添加取消请求的控制器
 const isMountedRef = React.useRef(true); // 跟踪组件是否已挂载



 // Tooltip状态
 const [tooltip, setTooltip] = useState({
 visible: false,
 x: 0,
 y: 0,
 fleetInfo: null
 });
 
 // 过期弹窗状态
 const [expiredModal, setExpiredModal] = useState({
 visible: false,
 fleetName: '',
 expiryDate: null
 });

 // 手动跳转弹窗状态
 const [manualRedirectModal, setManualRedirectModal] = useState({
 visible: false,
 bridgeUrl: '',
 fleetName: ''
 });

 // 线路切换 - us-direct | hk-cn2（Claude/Gemini/Grok 共用）
 // 美国直连放前面作为推荐项（更稳定），香港中转放后面（更易连接）
 const [activeLine, setActiveLine] = useState('us-direct');
 const lineOptions = [
 { id: 'us-direct', name: '美国 CN2 直连', subtitle: '更稳定', dotColor: 'bg-sky-500' },
 { id: 'hk-cn2', name: '香港 CN2 中转', subtitle: '更易连', dotColor: 'bg-emerald-500' }
 ];
 const LINE_TAB_FLEETS = ['claude', 'gemini', 'grok'];
 const CLAUDE_LINES = [
   { id: 'us-direct', name: '美国 CN2 直连', dotColor: 'bg-sky-500' },
   { id: 'hk-cn2',   name: '香港 CN2 中转', dotColor: 'bg-emerald-500' },
   { id: 'us-line3', name: '美国线路3',      dotColor: 'bg-violet-500' },
   { id: 'us-line4', name: '美国线路4',      dotColor: 'bg-amber-500' },
 ];
 const CLAUDE_LINE_BASE_URLS = {
   'us-direct': 'https://c.aimonkey.plus',
   'hk-cn2':   'https://chk.aimonkey.plus',
   'us-line3': 'https://c3.aimonkey.plus',
   'us-line4': 'https://c4.aimonkey.plus',
 };
 const [lineMode, setLineMode] = useState('auto');
 const [speedTestOverlay, setSpeedTestOverlay] = useState({
   visible: false, lines: [], bestLine: null,
 });
 
 // 获取用户信息
 const userInfo = useSelector(state => state.user.user);

 // 监听语言变化，强制重新渲染
 useEffect(() => {
 const handleLanguageChange = () => {
 setForceUpdate(prev => prev + 1);
 };

 // 监听i18n语言变化事件
 i18n.on('languageChanged', handleLanguageChange);
 
 // 监听自定义语言变化事件
 window.addEventListener('languageChanged', handleLanguageChange);

 return () => {
 i18n.off('languageChanged', handleLanguageChange);
 window.removeEventListener('languageChanged', handleLanguageChange);
 };
 }, [i18n]);
 
 // 拉取后端下发的车队提醒文案（按 type 索引）
 // 注意：与登录态解耦——不论用户是否登录都发起请求；每 20 分钟轮询一次
 useEffect(() => {
 let cancelled = false;
 const fetchWebInfo = async () => {
 try {
 const response = await fetch('/testapi/user/user_controller/web_info', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' }
 });
 const data = await response.json();
 if (cancelled) return;
 if (data.code === '0' && Array.isArray(data.info)) {
 // 同一个 type 可能下发多条，按 type 聚合成数组
 const map = {};
 data.info.forEach(item => {
 if (item && item.type && item.info) {
 if (!map[item.type]) map[item.type] = [];
 map[item.type].push(item.info);
 }
 });
 setWebInfoTips(map);
 } else {
 console.warn('[AIFleetGrid] /web_info 返回结构不符或 code 非 0:', data);
 }
 } catch (error) {
 console.error('[AIFleetGrid] /web_info 请求失败:', error);
 }
 };
 fetchWebInfo(); // 立即取一次
 const intervalId = setInterval(fetchWebInfo, 20 * 60 * 1000); // 每 20 分钟轮询
 return () => {
 cancelled = true;
 clearInterval(intervalId);
 };
 }, []);

 // 组件卸载时清理
 useEffect(() => {
 isMountedRef.current = true;
 return () => {
 isMountedRef.current = false;
 // 清理超时定时器
 if (cleanupTimeoutRef.current) {
 clearTimeout(cleanupTimeoutRef.current);
 cleanupTimeoutRef.current = null;
 }
 // 取消所有进行中的请求
 if (abortControllerRef.current) {
 abortControllerRef.current.abort();
 abortControllerRef.current = null;
 }
 };
 }, []);

 // 页面刷新时自动刷新用户信息
 useEffect(() => {
 const refreshUserInfo = async () => {
 // 只有在用户已登录时才刷新用户信息
 if (isLoggedIn) {
 try {
 console.log('🔄 AI Fleet页面刷新用户信息...');
 await dispatch(fetchUserProfile()).unwrap();
 console.log('✅ 用户信息刷新成功');
 } catch (error) {
 console.error('❌ 刷新用户信息失败:', error);
 // 刷新失败不影响页面正常使用，只是用户信息可能不是最新的
 }
 }
 };

 refreshUserInfo();
 }, []); // 只在组件挂载时执行一次

 // 根据AI类型映射图标、颜色、图片和背景色
 const getAIStyle = (type) => {
 const styles = {
 'claude': {
 icon: Cpu,
 gradient: 'from-accent-500 to-accent-600',
 name: t('aiFleet.fleetNames.claudePro'),
 image: '/images/claude-main.jpg',
 bgColor: 'bg-accent-50',
 borderColor: 'border-accent-200'
 },
 'claude-v2': {
 icon: Cpu,
 gradient: 'from-purple-500 to-purple-600',
 name: '克劳德·号池2',
 image: '/images/claude-main.jpg',
 bgColor: 'bg-purple-50',
 borderColor: 'border-purple-200'
 },
 'gemini': {
 icon: Globe,
 gradient: 'from-green-500 to-green-600',
 name: t('aiFleet.fleetNames.geminiPro'),
 image: '/images/gemini-main.jpg',
 bgColor: 'bg-green-50', // 绿色背景
 borderColor: 'border-green-200'
 },
 'sora': {
 icon: Globe,
 gradient: 'from-blue-500 to-blue-600',
 name: t('aiFleet.fleetNames.sora'),
 image: '/images/sora-main.jpg',
 bgColor: 'bg-blue-50', // 蓝色背景
 borderColor: 'border-blue-200'
 },
 'grok': {
 icon: Activity,
 gradient: 'from-accent-500 to-accent-600',
 name: t('aiFleet.fleetNames.grokSuper'),
 image: '/images/grok-main.jpg',
 bgColor: 'bg-accent-50', // 橙色背景（主色调）
 borderColor: 'border-accent-200'
 },
 'gpt-backup': {
 icon: Bot,
 gradient: 'from-gray-500 to-gray-600',
 name: t('aiFleet.fleetNames.gptPlus'),
 image: '/images/gpt-main.jpg',
 bgColor: 'bg-cream-100', // 灰色背景
 borderColor: 'border-ink-200'
 },
 'default': {
 icon: Bot,
 gradient: 'from-gray-500 to-gray-600',
 name: t('aiFleet.fleetNames.aiFleet'),
 image: '/images/gpt-main.jpg',
 bgColor: 'bg-cream-100', // 灰色背景
 borderColor: 'border-ink-200'
 }
 };
 return styles[type] || styles.default;
 };

 // 转换Sora API数据为统一格式
 const convertSoraData = (soraData) => {
 if (!soraData || !soraData.list) return [];
 
 // 先转换数据
 const convertedData = soraData.list.map((node, index) => {
 // 根据颜色映射状态文字
 let statusText = '';
 if (node.color === 'green') {
 statusText = t('aiFleet.busyLevel.idle');
 } else if (node.color === 'yellow') {
 statusText = t('aiFleet.busyLevel.moderate');
 } else if (node.color === 'red' || node.status !== 1) {
 statusText = t('aiFleet.status.stopped');
 } else {
 statusText = t('aiFleet.busyLevel.idle'); // 默认为空闲
 }
 
 return {
 id: node.carID,
 name: `${t('aiFleet.nodeInfo.nodePrefix')} ${index + 1}`,
 status: node.color === 'red' ? 'maintenance' : (node.status === 1 ? 'online' : 'offline'),
 busyLevel: Math.round(Math.min(((node.count || 0) / 20) * 100, 100)), // Sora: count÷20
 description: statusText,
 features: [
 node.isPlus ? `${t('aiFleet.accountTypes.plus')}${t('common.version')}` : '',
 node.isPro ? `${t('aiFleet.accountTypes.pro')}${t('common.version')}` : '',
 `${node.count || 0}${t('aiFleet.nodeInfo.peopleUsing')}`
 ].filter(Boolean),
 region: t('aiFleet.regions.global'),
 speed: node.color === 'green' ? t('aiFleet.responses.ultraFast') : node.color === 'yellow' ? t('aiFleet.responses.normal') : t('aiFleet.responses.slow'),
 price: node.isPlus ? `¥300${t('aiFleet.pricing.perMonth')}` : `¥150${t('aiFleet.pricing.perMonth')}`,
 isPlus: node.isPlus,
 isPro: node.isPro,
 color: node.color || 'green',
 count: node.count || 0 // 保留原始count用于排序
 };
 });
 
 // 排序：先按Plus/普通分组，再按使用率排序
 return convertedData.sort((a, b) => {
 // 首先，Plus账号排在前面
 if ((a.isPlus || a.isPro) && !(b.isPlus || b.isPro)) return -1;
 if (!(a.isPlus || a.isPro) && (b.isPlus || b.isPro)) return 1;
 
 // 同组内，按使用人数从少到多排序
 return a.count - b.count;
 });
 };

 // 转换Grok API数据为统一格式
 const convertGrokData = (grokData) => {
 if (!grokData || !grokData.list) return [];
 
 // 先转换数据
 const convertedData = grokData.list.map((node, index) => {
 // 根据颜色映射状态文字
 let statusText = '';
 if (node.color === 'green') {
 statusText = t('aiFleet.busyLevel.idle');
 } else if (node.color === 'yellow') {
 statusText = t('aiFleet.busyLevel.moderate');
 } else if (node.color === 'red' || node.status !== 1) {
 statusText = t('aiFleet.status.stopped');
 } else {
 statusText = t('aiFleet.busyLevel.idle'); // 默认为空闲
 }
 
 return {
 id: node.carID,
 name: `${t('aiFleet.nodeInfo.nodePrefix')} ${index + 1}`,
 status: node.color === 'red' ? 'maintenance' : (node.status === 1 ? 'online' : 'offline'),
 busyLevel: Math.round(Math.min(((node.count || 0) / 50) * 100, 100)), // Grok: count÷50
 description: statusText,
 features: [
 node.isSuper ? t('aiFleet.accountTypes.superAccount') : t('aiFleet.accountTypes.freeAccount'),
 `${node.count || 0}${t('aiFleet.nodeInfo.peopleUsing')}`
 ].filter(Boolean),
 region: t('aiFleet.regions.global'),
 speed: node.color === 'green' ? t('aiFleet.responses.ultraFast') : node.color === 'yellow' ? t('aiFleet.responses.normal') : t('aiFleet.responses.slow'),
 price: node.isSuper ? `¥299${t('aiFleet.pricing.perMonth')}` : t('aiFleet.pricing.free'),
 isSuper: node.isSuper,
 label: node.label,
 color: node.color,
 count: node.count || 0 // 保留原始count用于排序
 };
 });
 
 // 排序：先按Super/Free分组，再按使用率排序
 return convertedData.sort((a, b) => {
 // 首先，Super账号排在前面
 if (a.isSuper && !b.isSuper) return -1;
 if (!a.isSuper && b.isSuper) return 1;
 
 // 同组内，按使用人数从少到多排序
 return a.count - b.count;
 });
 };

 // 转换Claude API数据为统一格式
 const convertClaudeData = (claudeData) => {
 if (!claudeData || !claudeData.list) return [];
 
 // 先转换数据
 const convertedData = claudeData.list.map((node, index) => {
 // 根据API字段判断账号类型
 let accountType = '';
 let accountLabel = '';
 if (node.isMax) {
 accountType = 'max';
 accountLabel = 'MAX';
 } else if (node.isPro) {
 accountType = 'pro';
 accountLabel = 'PRO';
 } else {
 accountType = 'free';
 accountLabel = 'FREE';
 }

 // 简化状态逻辑：只要不是red就按green处理
 let statusText = '';
 let normalizedColor = '';
 if (node.color === 'red' || node.status !== 1) {
 statusText = t('aiFleet.status.stopped');
 normalizedColor = 'red';
 } else {
 // 非red状态统一按green处理
 statusText = t('aiFleet.busyLevel.idle');
 normalizedColor = 'green';
 }

 // MAX账号使用不同的分母计算繁忙度
 let busyLevel;
 if (accountType === 'max') {
 // MAX账号: count÷200
 busyLevel = Math.round(Math.min(((node.count || 0) / 500) * 100, 100));
 } else {
 // 其他账号: count÷40
 busyLevel = Math.round(Math.min(((node.count || 0) / 40) * 100, 100));
 }

 return {
 id: node.carID,
 name: `${t('aiFleet.nodeInfo.nodePrefix')} ${index + 1}`,
 status: normalizedColor === 'red' ? 'maintenance' : (node.status === 1 ? 'online' : 'offline'),
 busyLevel: busyLevel,
 description: statusText,
 features: [
 accountType === 'max' ? 'Max账号' :
 accountType === 'pro' ? 'Pro账号' : '免费账号',
 `${node.count || 0}${t('aiFleet.nodeInfo.peopleUsing')}`
 ].filter(Boolean),
 region: t('aiFleet.regions.global'),
 speed: normalizedColor === 'green' ? t('aiFleet.responses.ultraFast') : t('aiFleet.responses.slow'),
 price: accountType === 'max' ? '¥300/月' :
 accountType === 'pro' ? '¥200/月' : '免费',

 // Claude车队专用字段
 isMax: node.isMax,
 isPro: node.isPro,
 accountType: accountType,
 label: accountLabel,
 color: normalizedColor, // 使用标准化后的颜色
 count: node.count || 0, // 保留原始count用于排序
 clears_in: node.clears_in || 0 // 停运恢复时间
 };
 });
 
 // 排序：先按Max/Pro/Free分组，再按使用率排序
 return convertedData.sort((a, b) => {
 // 首先，Max账号排在最前面
 if (a.accountType === 'max' && b.accountType !== 'max') return -1;
 if (a.accountType !== 'max' && b.accountType === 'max') return 1;
 
 // 然后，Pro账号排在Free前面
 if (a.accountType === 'pro' && b.accountType === 'free') return -1;
 if (a.accountType === 'free' && b.accountType === 'pro') return 1;
 
 // 同组内，按使用人数从少到多排序
 return a.count - b.count;
 });
 };

 // 转换新线路(claude-v2) API数据为统一格式
 // 接口字段：carID, status(1=在线), type("free"|"team-free")
 const convertClaudeV2Data = (claudeV2Data) => {
 if (!claudeV2Data || !claudeV2Data.list) return [];

 const convertedData = claudeV2Data.list.map((node, index) => {
 const isTeam = node.type === 'team-free';
 // status: 1=空闲推荐(green) 2=繁忙(yellow) 3=不可用(red)
 const color = node.status === 1 ? 'green' : node.status === 2 ? 'yellow' : 'red';
 const nodeStatus = node.status === 3 ? 'maintenance' : 'online';
 const description = node.status === 1 ? t('aiFleet.busyLevel.idleRecommended')
 : node.status === 2 ? t('aiFleet.busyLevel.busy')
 : t('aiFleet.status.stopped');
 const busyLevel = node.status === 1 ? 20 : node.status === 2 ? 70 : 100;

 return {
 id: node.carID,
 name: `${t('aiFleet.nodeInfo.nodePrefix')} ${index + 1}`,
 status: nodeStatus,
 busyLevel: busyLevel,
 description: description,
 features: [isTeam ? 'Max账号' : '免费账号'],
 region: t('aiFleet.regions.global'),
 speed: node.status === 1 ? t('aiFleet.responses.ultraFast') : node.status === 2 ? t('aiFleet.responses.normal') : t('aiFleet.responses.slow'),
 price: '免费',
 isTeam: isTeam,
 accountType: node.type,
 label: isTeam ? 'MAX' : null,
 color: color,
 count: 0
 };
 });

 // Team账号排前面
 return convertedData.sort((a, b) => {
 if (a.isTeam && !b.isTeam) return -1;
 if (!a.isTeam && b.isTeam) return 1;
 return 0;
 });
 };

 // 转换Gemini API数据为统一格式
 const convertGeminiData = (geminiData) => {
 if (!geminiData || !geminiData.list) return [];

 // 先转换数据
 const convertedData = geminiData.list.map((node, index) => {
 // 根据颜色映射状态文字
 let statusText = '';
 if (node.color === 'green') {
 statusText = t('aiFleet.busyLevel.idle');
 } else if (node.color === 'yellow') {
 statusText = t('aiFleet.busyLevel.moderate');
 } else if (node.color === 'red' || node.status !== 1) {
 statusText = t('aiFleet.status.stopped');
 } else {
 statusText = t('aiFleet.busyLevel.idle'); // 默认为空闲
 }

 // 根据API的label字段判断账号类型
 const isPro = node.label === 'PRO' || node.isPro;
 const isUltra = node.isUltra || false;

 return {
 id: node.carID,
 name: `${t('aiFleet.nodeInfo.nodePrefix')} ${index + 1}`,
 status: node.color === 'red' ? 'maintenance' : (node.status === 1 ? 'online' : 'offline'),
 busyLevel: Math.round(Math.min(((node.count || 0) / 50) * 100, 100)), // Gemini: count÷50
 description: statusText,
 features: [
 isPro ? `${t('aiFleet.accountTypes.pro')}${t('common.version')}` :
 isUltra ? 'Ultra版' :
 t('aiFleet.accountTypes.freeAccount'),
 `${node.count || 0}${t('aiFleet.nodeInfo.peopleUsing')}`
 ].filter(Boolean),
 region: t('aiFleet.regions.global'),
 speed: node.color === 'green' ? t('aiFleet.responses.ultraFast') : node.color === 'yellow' ? t('aiFleet.responses.normal') : t('aiFleet.responses.slow'),
 price: isPro ? `¥199${t('aiFleet.pricing.perMonth')}` : isUltra ? `¥299${t('aiFleet.pricing.perMonth')}` : t('aiFleet.pricing.free'),
 isPro: isPro,
 isUltra: isUltra,
 label: node.label || 'free', // 保存原始label字段
 color: node.color || 'green', // 保存原始color字段
 count: node.count || 0 // 保留原始count用于排序
 };
 });

 // 排序：按count从小到大排序
 return convertedData.sort((a, b) => {
 return a.count - b.count;
 });
 };

 // 转换GPT Plus车队API数据为统一格式
 const convertGptBackupData = (gptData) => {
 if (!gptData || !gptData.list) return [];
 
 // 先转换数据
 const convertedData = gptData.list.map((node, index) => {
 // 根据颜色映射状态文字
 let statusText = '';
 if (node.color === 'green') {
 statusText = t('aiFleet.busyLevel.idle');
 } else if (node.color === 'yellow') {
 statusText = t('aiFleet.busyLevel.moderate');
 } else if (node.color === 'red' || node.status !== 1) {
 statusText = t('aiFleet.status.stopped');
 } else {
 statusText = t('aiFleet.busyLevel.idle'); // 默认为空闲
 }

 // 使用真实API数据
 // iqStatus: "1" = 满血，其他值 = 降智
 const isFullPower = node.iqStatus === "1";
 
 // renewal_status: 1 = 月续，0 = 不续费
 const autoRenewal = node.renewal_status === 1;
 
 // 从API获取真实到期时间
 const expirationDate = node.expiresAt;
 
 // 账号类型从API的label字段获取
 const accountType = node.label ? node.label.toLowerCase() : 'free'; // TEAM -> team, PLUS -> plus
 const isTeam = node.label === 'TEAM';
 
 return {
 id: node.carID,
 name: `${t('aiFleet.nodeInfo.nodePrefix')} ${index + 1}`,
 status: node.color === 'red' ? 'maintenance' : (node.status === 1 ? 'online' : 'offline'),
 busyLevel: Math.round(Math.min(((node.count || 0) / 100) * 100, 100)), // GPT备用: count÷100
 description: statusText,
 features: [
 node.isSuper ? t('aiFleet.accountTypes.plusAccount') : t('aiFleet.accountTypes.regularAccount'),
 `${node.count || 0}${t('aiFleet.nodeInfo.peopleUsing')}`
 ].filter(Boolean),
 region: t('aiFleet.regions.global'),
 speed: node.color === 'green' ? t('aiFleet.responses.ultraFast') : node.color === 'yellow' ? t('aiFleet.responses.normal') : t('aiFleet.responses.slow'),
 price: node.isSuper ? `¥120${t('aiFleet.pricing.perMonth')}` : `¥60${t('aiFleet.pricing.perMonth')}`,
 isSuper: node.isSuper,
 label: node.label,
 color: node.color,
 count: node.count || 0, // 保留原始count用于排序

 // GPT车队专用的标签数据 - 使用真实API数据
 accountType: accountType,
 isTeam: isTeam,
 intelligenceLevel: isFullPower ? 'full' : 'degraded',
 isFullPower: isFullPower, // iqStatus === "1" 为满血，否则为降智
 renewalType: autoRenewal ? 'monthly' : 'expire',
 autoRenewal: autoRenewal, // renewal_status === 1 为月续，否则为不续费
 expirationDate: expirationDate // 使用API返回的真实到期时间
 };
 });
 
 // 过滤掉FREE账号，只保留TEAM和PLUS
 const filteredData = convertedData.filter(node => 
 node.label === 'TEAM' || node.label === 'PLUS'
 );
 
 // 排序：先按满血/降智状态，再按使用人数排序
 return filteredData.sort((a, b) => {
 // 首先，满血账号排在降智账号前面
 if (a.isFullPower && !b.isFullPower) return -1;
 if (!a.isFullPower && b.isFullPower) return 1;
 
 // 同状态内按使用人数从少到多排序
 return a.count - b.count;
 });
 };

 // 车队数据 - 从配置文件读取 - 简化版本，不支持多语言
 const fleetInfo = React.useMemo(() => {
 return Object.values(fleetCardsConfig.fleetCards).map(fleet => ({
 id: fleet.id,
 name: fleet.name,
 type: fleet.type,
 status: fleet.status
 }));
 }, [forceUpdate]); // 依赖于forceUpdate

 // 获取节点数据
 const fetchNodeData = async (fleet, retryCount = 0) => {
 if (!fleet || !isMountedRef.current) return;
 
 // 清理之前的超时
 if (cleanupTimeoutRef.current) {
 clearTimeout(cleanupTimeoutRef.current);
 cleanupTimeoutRef.current = null;
 }
 
 // 取消之前的请求
 if (abortControllerRef.current) {
 abortControllerRef.current.abort();
 }
 
 // 创建新的AbortController
 abortControllerRef.current = new AbortController();
 
 if (isMountedRef.current) {
 setLoadingNodes(true);
 setError(''); // 清除之前的错误
 }
 
 // 设置5秒超时定时器
 const timeoutId = setTimeout(() => {
 if (!isMountedRef.current) return;
 
 console.log(`加载${fleet.name}节点数据超时，准备重试...`);
 
 // 检查是否还需要重试
 if (retryCount < 3) {
 console.log(`第${retryCount + 1}次重试加载${fleet.name}节点数据`);
 fetchNodeData(fleet, retryCount + 1);
 } else {
 console.error(`加载${fleet.name}节点数据失败，已重试${retryCount}次`);
 if (isMountedRef.current) {
 setLoadingNodes(false);
 setError(`加载${fleet.name}节点数据超时，请稍后重试`);
 setNodeData(prev => ({
 ...prev,
 [fleet.id]: []
 }));
 }
 }
 }, 5000); // 5秒超时
 
 cleanupTimeoutRef.current = timeoutId;
 
 try {
 let data = [];
 
 switch(fleet.type) {
 case 'sora':
 const soraResponse = await getSoraCarList({}, { signal: abortControllerRef.current?.signal });
 if (soraResponse && soraResponse.code === 1 && soraResponse.data) {
 data = convertSoraData(soraResponse.data);
 }
 break;
 
 case 'claude':
 const claudeResponse = await getCCarList({}, { signal: abortControllerRef.current?.signal });
 if (claudeResponse && claudeResponse.code === 1 && claudeResponse.data) {
 data = convertClaudeData(claudeResponse.data);
 }
 break;

 case 'claude-v2':
 const claudeV2Response = await getClaudeV2CarList({}, { signal: abortControllerRef.current?.signal });
 if (claudeV2Response && claudeV2Response.code === 1000 && claudeV2Response.data) {
 data = convertClaudeV2Data(claudeV2Response.data);
 }
 break;

 case 'gemini':
 const geminiResponse = await getGeminiCarList({}, { signal: abortControllerRef.current?.signal });
 if (geminiResponse && geminiResponse.code === 1 && geminiResponse.data) {
 data = convertGeminiData(geminiResponse.data);
 }
 break;

 case 'grok':
 const grokResponse = await getGrokCarList({}, { signal: abortControllerRef.current?.signal });
 if (grokResponse && grokResponse.code === 1 && grokResponse.data) {
 data = convertGrokData(grokResponse.data);
 }
 break;
 
 case 'gpt-backup':
 // GPT Plus车队使用getGptCarList API
 const gptBackupResponse = await getGptCarList({}, { signal: abortControllerRef.current?.signal });
 if (gptBackupResponse && gptBackupResponse.code === 1 && gptBackupResponse.data) {
 data = convertGptBackupData(gptBackupResponse.data);
 }
 break;
 
 default:
 data = [];
 }
 
 // 清除超时定时器
 if (cleanupTimeoutRef.current) {
 clearTimeout(cleanupTimeoutRef.current);
 cleanupTimeoutRef.current = null;
 }
 
 // 只在组件仍然挂载时更新状态
 if (isMountedRef.current) {
 setNodeData(prev => ({
 ...prev,
 [fleet.id]: data
 }));
 setLoadingNodes(false);
 setError(''); // 清除错误信息
 }
 } catch (error) {
 // 清除超时定时器
 if (cleanupTimeoutRef.current) {
 clearTimeout(cleanupTimeoutRef.current);
 cleanupTimeoutRef.current = null;
 }
 
 // 如果是取消请求的错误，忽略
 if (error.name === 'AbortError' || !isMountedRef.current) {
 return;
 }
 
 console.error('获取节点数据失败:', error);
 
 // 如果是网络错误且重试次数小于3次，自动重试
 if (retryCount < 3 && isMountedRef.current) {
 console.log(`获取${fleet.name}节点数据出错，第${retryCount + 1}次重试...`);
 setTimeout(() => {
 if (isMountedRef.current) {
 fetchNodeData(fleet, retryCount + 1);
 }
 }, 1000); // 1秒后重试
 } else if (isMountedRef.current) {
 setNodeData(prev => ({
 ...prev,
 [fleet.id]: []
 }));
 setLoadingNodes(false);
 setError(`${t('aiFleet.error')} ${fleet.name}`);
 }
 }
 };

 // 组件初始化时设置车队列表
 useEffect(() => {
 setFleets(fleetInfo);
 setLoading(false);
 }, []);

 // 初始化时选中第一个车队或默认指定的车队
 useEffect(() => {
 if (fleets.length > 0 && !selectedFleet) {
 const defaultFleet = fleets.find(f => f.type === activeTab) || fleets[0];
 setSelectedFleet(defaultFleet);
 }
 }, [fleets, activeTab, selectedFleet]);

 // 当选中车队变化时，获取节点数据并启动轮询
 useEffect(() => {
 if (selectedFleet && isMountedRef.current) {
 // 取消之前的所有请求和定时器
 if (cleanupTimeoutRef.current) {
 clearTimeout(cleanupTimeoutRef.current);
 cleanupTimeoutRef.current = null;
 }
 if (abortControllerRef.current) {
 abortControllerRef.current.abort();
 }

 fetchNodeData(selectedFleet, 0);
 }
 }, [selectedFleet]);

 // 当节点数据或筛选条件变化时，更新筛选结果
 useEffect(() => {
 if (selectedFleet && nodeData[selectedFleet.id]) {
 console.log('开始筛选节点:', {
 fleet: selectedFleet.type,
 nodeCount: nodeData[selectedFleet.id].length,
 activeFilters: activeFilters,
 hasActiveFilters: Object.values(activeFilters).filter(Boolean).length > 0
 });
 
 let rawNodes = nodeData[selectedFleet.id];
 const filtered = filterNodes(rawNodes, activeFilters, selectedFleet.type);
 console.log('筛选后节点数量:', filtered.length);
 setFilteredNodes(filtered);
 } else {
 console.log('没有车队数据或选中车队:', {
 selectedFleet: selectedFleet?.type,
 hasNodeData: !!nodeData[selectedFleet?.id]
 });
 setFilteredNodes([]);
 }
 }, [nodeData, activeFilters, selectedFleet]);

 const handleFleetClick = (fleet) => {
 try {
 console.log('选中车队:', fleet);
 
 // 取消之前的所有请求
 if (abortControllerRef.current) {
 abortControllerRef.current.abort();
 }
 
 // 切换车队时重置筛选状态 - 确保显示所有节点
 setActiveFilters({});
 
 // 直接选中车队，不检查过期
 setSelectedFleet(fleet);
 
 // 更新路由以反映当前选中的车队
 navigate(`/ai-fleet/${fleet.type}`, { replace: true });
 } catch (error) {
 console.error('切换车队时出错:', error);
 if (isMountedRef.current) {
 setError('切换车队失败，请重试');
 }
 }
 };



 // 处理鼠标进入卡片
 const handleMouseEnter = (e, fleet) => {
 // 初始位置设置在鼠标右下方，与handleMouseMove保持一致
 setTooltip({
 visible: true,
 x: e.clientX + 15, // 鼠标右边15px
 y: e.clientY + 15, // 鼠标下方15px
 fleetInfo: fleet
 });
 };

 // 处理鼠标移动
 const handleMouseMove = (e) => {
 // 检查鼠标是否在按钮区域
 const target = e.target;
 const isOverButton = target.tagName === 'BUTTON' || target.closest('button');
 
 // 检查鼠标是否还在卡片内
 const card = e.currentTarget;
 const rect = card.getBoundingClientRect();
 const isInsideCard = e.clientX >= rect.left && 
 e.clientX <= rect.right && 
 e.clientY >= rect.top && 
 e.clientY <= rect.bottom;
 
 if (!isInsideCard) {
 // 如果鼠标不在卡片内，立即隐藏tooltip
 setTooltip(prev => ({ ...prev, visible: false }));
 return;
 }
 
 if (isOverButton) {
 // 如果在按钮上，隐藏tooltip
 setTooltip(prev => ({ ...prev, visible: false }));
 } else if (tooltip.fleetInfo) {
 // 否则更新tooltip位置 - 始终保持在鼠标右下方
 setTooltip(prev => ({
 ...prev,
 visible: true,
 x: e.clientX + 15, // 向右偏移
 y: e.clientY + 15 // 向下偏移
 }));
 }
 };

 // 处理鼠标离开
 const handleMouseLeave = () => {
 setTooltip({
 visible: false,
 x: 0,
 y: 0,
 fleetInfo: null
 });
 };

 // 获取车队的提示信息 - 从配置文件读取 - 简化版本，不支持多语言
 const getFleetTooltipInfo = React.useMemo(() => {
 return (fleetType) => {
 const fleet = fleetCardsConfig.fleetCards[fleetType];
 if (!fleet || !fleet.tooltip) return {
 features: ['暂无信息'],
 price: '暂无信息',
 limits: ['暂无信息']
 };
 
 return fleet.tooltip;
 };
 }, [forceUpdate]);

 const handleRetry = () => {
 setError(''); // 清除错误信息
 if (selectedFleet) {
 fetchNodeData(selectedFleet, 0); // 重置重试次数
 }
 };

 // 筛选节点逻辑 - 基于当前车队类型
 const filterNodes = (nodes, filters, fleetType) => {
 if (!nodes || nodes.length === 0) return [];
 
 // 如果没有任何激活的筛选条件，返回所有节点
 const activeFilterValues = Object.values(filters).filter(Boolean);
 if (activeFilterValues.length === 0) {
 console.log('没有激活的筛选条件，返回所有节点:', nodes.length, '个节点');
 return nodes;
 }
 
 // 获取当前车队的筛选配置
 const fleetTags = fleetFilterTags[fleetType];
 if (!fleetTags) return nodes;
 
 return nodes.filter(node => {
 // 检查每个激活的筛选器
 return Object.entries(filters).every(([category, selectedTagId]) => {
 if (!selectedTagId || !fleetTags[category]) return true;
 
 const selectedTag = fleetTags[category].find(tag => tag.id === selectedTagId);
 if (!selectedTag) return true;
 
 return selectedTag.filter(node);
 });
 });
 };

 // 处理筛选标签点击
 const handleFilterClick = (category, tagId) => {
 setActiveFilters(prev => {
 // 如果点击的是当前选中的标签，取消选择
 if (prev[category] === tagId) {
 const newFilters = { ...prev };
 delete newFilters[category];
 return newFilters;
 }
 
 // 否则选择新标签
 return {
 ...prev,
 [category]: tagId
 };
 });
 };

 // 获取状态颜色和图标
 const getStatusInfo = (status) => {
 switch(status) {
 case 'online':
 return {
 color: 'text-green-600 bg-green-100',
 text: t('aiFleet.status.running'),
 icon: CheckCircle
 };
 case 'testing':
 return {
 color: 'text-blue-600 bg-blue-100',
 text: '内部测试',
 icon: Activity
 };
 case 'maintenance':
 return {
 color: 'text-red-600 bg-red-100',
 text: t('aiFleet.status.stopped'),
 icon: XCircle
 };
 case 'limited':
 return {
 color: 'text-yellow-600 bg-yellow-100',
 text: t('aiFleet.status.limitedRunning'),
 icon: AlertCircle
 };
 default:
 return {
 color: 'text-ink-600 bg-cream-100',
 text: t('aiFleet.status.offline'),
 icon: XCircle
 };
 }
 };

 // 根据繁忙程度获取进度条颜色
 const getBusyLevelStyle = (busyLevel, node) => {
 // 对于Grok，使用特殊的映射逻辑
 if (node && node.color) {
 if (node.color === 'green') {
 return {
 color: 'bg-green-500',
 barColor: 'bg-green-500',
 borderColor: 'border-green-200',
 text: '空闲推荐',
 label: '空闲',
 textColor: 'text-green-600'
 };
 } else if (node.color === 'yellow') {
 return {
 color: 'bg-yellow-500',
 barColor: 'bg-yellow-500',
 borderColor: 'border-yellow-200',
 text: '适中负载',
 label: '适中',
 textColor: 'text-yellow-600'
 };
 } else if (node.color === 'red') {
 return {
 color: 'bg-red-500',
 barColor: 'bg-red-500',
 borderColor: 'border-red-200',
 text: '停运',
 label: '停运',
 textColor: 'text-red-600'
 };
 }
 }
 
 // 默认根据使用率判断
 if (busyLevel <= 30) {
 return {
 color: 'bg-green-500',
 barColor: 'bg-green-500',
 borderColor: 'border-green-200',
 text: t('aiFleet.busyLevel.idleRecommended'),
 label: t('aiFleet.busyLevel.idle'),
 textColor: 'text-green-600'
 };
 } else if (busyLevel <= 70) {
 return {
 color: 'bg-yellow-500',
 barColor: 'bg-yellow-500',
 borderColor: 'border-yellow-200',
 text: t('aiFleet.busyLevel.moderateLoad'),
 label: t('aiFleet.busyLevel.moderate'),
 textColor: 'text-yellow-600'
 };
 } else {
 return {
 color: 'bg-red-500',
 barColor: 'bg-red-500',
 borderColor: 'border-red-200',
 text: t('aiFleet.busyLevel.busyState'),
 label: t('aiFleet.busyLevel.busy'),
 textColor: 'text-red-600'
 };
 }
 };

 // Loading组件
 const LoadingSpinner = () => (
 <div className="flex items-center justify-center py-24">
 <div className="flex flex-col items-center gap-6">
 <div className="relative">
 <div className="w-20 h-20 border-4 border-ink-200 rounded-full"></div>
 <div className="w-20 h-20 border-4 border-accent-500 rounded-full border-t-transparent animate-spin absolute inset-0"></div>
 </div>
 <div className="text-center">
 <p className="text-lg font-medium text-ink-700">{t('aiFleet.loading')}</p>
 <p className="text-sm text-ink-500 mt-1">{t('common.loading')}</p>
 </div>
 </div>
 </div>
 );

 // 错误提示组件
 const ErrorMessage = () => (
 <div className="flex items-center justify-center py-12">
 <div className="text-center">
 <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
 <h3 className="text-lg font-semibold text-ink-900 mb-2">{t('aiFleet.loadFailed')}</h3>
 <p className="text-ink-600 mb-4">{error}</p>
 <button
 onClick={handleRetry}
 className="inline-flex items-center gap-2 bg-accent-500 text-white px-4 py-2 rounded-lg hover:bg-accent-600 transition-colors duration-300"
 >
 <RefreshCw className="w-4 h-4" />
 {t('aiFleet.retry')}
 </button>
 </div>
 </div>
 );

 // 空状态组件
 const EmptyState = () => (
 <div className="flex items-center justify-center py-12">
 <div className="text-center">
 <div className="w-24 h-24 bg-ink-100 rounded-full flex items-center justify-center mx-auto mb-6">
 <Bot className="w-12 h-12 text-ink-400" />
 </div>
 <h3 className="text-xl font-bold text-ink-900 mb-4">{t('aiFleet.noFleets')}</h3>
 <p className="text-ink-600 mb-4">{t('aiFleet.noFleetsDesc')}</p>
 <button
 onClick={handleRetry}
 className="inline-flex items-center gap-2 bg-accent-500 text-white px-4 py-2 rounded-lg hover:bg-accent-600 transition-colors duration-300"
 >
 <RefreshCw className="w-4 h-4" />
 {t('aiFleet.refresh')}
 </button>
 </div>
 </div>
 );

 // 节点网格组件
 const NodeGrid = ({ nodes, fleetType }) => {
 if (!nodes || nodes.length === 0) {
 return (
 <div className="text-center py-8">
 <p className="text-ink-500">{t('aiFleet.nodeInfo.noAvailableNodes')}</p>
 </div>
 );
 }

 const aiStyle = getAIStyle(fleetType);

 return (
 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4 mt-4 sm:mt-6">
 {nodes.map((node, index) => {
 const statusInfo = getStatusInfo(node.status);
 const busyStyle = getBusyLevelStyle(node.busyLevel, node);
 
 return (
 <div
 key={node.id}
 className={`bg-cream-50 rounded-xl border border-ink-200 hover:border-accent-500 transition-colors duration-200 overflow-hidden group relative ${
 fleetType === 'gpt-backup' ? 'sm:min-h-[280px]' : ''
 }`}
 >
 {/* 状态指示：左上角小圆点（替代原顶部彩色条） */}
 <div className="absolute top-2.5 left-2.5 z-20">
 <span className="relative flex w-2 h-2">
 {node.status !== 'maintenance' && (
 <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-40"></span>
 )}
 <span className={`relative block w-2 h-2 rounded-full ${
 node.status === 'maintenance' ? 'bg-red-500' : 'bg-green-500'
 }`}></span>
 </span>
 </div>
 
 {/* 顶部图片区域 - GPT车队稍高，统一暖米底 */}
 <div className={`relative ${fleetType === 'gpt-backup' ? 'h-20' : 'h-16'} bg-cream-100 border-b border-ink-200 flex items-center justify-between px-3`}>
 {/* 左侧：Logo */}
 <div className="flex items-center gap-2">
 <div className="w-12 h-12 rounded-lg overflow-hidden bg-cream-50 border border-ink-200 flex items-center justify-center">
 <img 
 src={aiStyle.image}
 alt={aiStyle.name}
 className="w-10 h-10 object-contain"
 onError={(e) => {
 e.target.style.display = 'none';
 e.target.nextSibling.style.display = 'flex';
 }}
 />
 <div
 className="w-10 h-10 bg-accent-500 rounded flex items-center justify-center"
 style={{display: 'none'}}
 >
 {React.createElement(aiStyle.icon, { className: "w-5 h-5 text-white" })}
 </div>
 </div>
 </div>
 
 {/* 右侧：GPT车队横排标签区域 - 优化为更紧凑布局 */}
 {fleetType === 'gpt-backup' ? (
 <div className="flex items-center gap-1 flex-wrap justify-end">
 {/* 账号类型标签 */}
 <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
 node.label === 'TEAM' ? 'bg-accent-500 text-white' :
 node.label === 'PLUS' ? 'bg-accent-500 text-white' :
 'bg-ink-600 text-white'
 }`}>
 {node.label || 'FREE'}
 </span>
 
 {/* 降智/满血标签 - 缩小间距和padding */}
 <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
 node.isFullPower ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'
 }`}>
 {node.isFullPower ? '满血' : '降智'}
 </span>
 
 {/* 状态标签 - 最紧凑 */}
 <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
 node.color === 'green' ? 'bg-green-600 text-white' :
 node.color === 'yellow' ? 'bg-yellow-600 text-white' :
 'bg-red-600 text-white'
 }`}>
 {node.color === 'green' ? '空闲' :
 node.color === 'yellow' ? '适中' : '繁忙'}
 </span>
 </div>
 ) : (
 /* 其他车队保持原有的单标签 */
 ((fleetType === 'claude-v2' && node.isTeam) || node.isSuper !== undefined || node.isPlus !== undefined || node.isPro !== undefined || node.isMax !== undefined || node.isUltra !== undefined) && (
 <div className={`px-3 py-1 rounded-md text-xs font-bold ${
 // 新线路(claude-v2)：MAX用accent色
 fleetType === 'claude-v2' ? (
 'bg-accent-500 text-white'
 ) :
 // 老线路(claude)标签颜色
 fleetType === 'claude' ? (
 node.isMax ? 'bg-accent-500 text-white' :
 node.isPro ? 'bg-accent-500 text-white' :
 'bg-green-500 text-white'
 ) :
 // Gemini车队的标签颜色 - 根据label字段
 fleetType === 'gemini' ? (
 node.label === 'PRO' ? 'bg-accent-500 text-white' :
 node.label === 'ULTRA' ? 'bg-accent-500 text-white' :
 'bg-green-500 text-white'
 ) :
 // Grok车队：SUPER 紫 / FREE 绿
 fleetType === 'grok' ? (
 node.isSuper ? 'bg-accent-500 text-white' : 'bg-green-500 text-white'
 ) : (
 node.isSuper || node.isPlus || node.isPro
 ? 'bg-accent-500 text-white'
 : 'bg-accent-600 text-white'
 )
 }`}>
 {fleetType === 'claude-v2' ? (
 'MAX'
 ) :
 fleetType === 'claude' ? (
 node.isMax ? 'MAX' :
 node.isPro ? 'PRO' : 'FREE'
 ) :
 fleetType === 'gemini' ? (
 node.label || 'FREE'
 ) :
 fleetType === 'grok' ? (
 node.isSuper ? 'SUPER' : 'FREE'
 ) : (
 fleetType === 'sora' && node.isPlus ? 'PLUS' :
 node.isSuper ? 'SUPER' :
 'FREE'
 )}
 </div>
 )
 )}
 </div>
 
 <div className={`${fleetType === 'gpt-backup' ? 'p-3' : 'p-2 sm:p-3'}`}>
 {/* 节点ID */}
 <div className="text-sm sm:text-base font-bold text-ink-900 mb-2 text-center">
 {node.id}
 </div>

 {/* GPT车队：底部信息区域 - 优化为更紧凑布局 */}
 {fleetType === 'gpt-backup' && (
 <div className="mb-2 space-y-1.5">
 {/* 到期时间 - 更紧凑设计 */}
 <div className="flex justify-center">
 <div className="bg-cream-100 border border-ink-200 rounded px-2 py-1 flex items-center gap-1">
 <Calendar className="w-3 h-3 text-ink-600" />
 <span className="text-xs text-ink-700">
 {new Date(node.expirationDate).toLocaleDateString('zh-CN', {
 month: '2-digit',
 day: '2-digit'
 })}到期
 </span>
 </div>
 </div>

 {/* 续费机制 - 更紧凑设计 */}
 <div className="flex justify-center">
 <span className="bg-cream-100 border border-ink-200 rounded px-2 py-1 text-xs text-ink-700 flex items-center gap-1">
 {node.autoRenewal ? '🔄' : '⏰'}
 {node.autoRenewal ? '月续' : '不续'}
 </span>
 </div>
 </div>
 )}
 
 {/* 状态描述 - 只对非GPT车队显示，GPT车队已在header显示 */}
 {fleetType !== 'gpt-backup' && (
 <div className="text-center mb-2">
 <span className={`inline-flex items-center gap-1.5 text-xs ${
 node.status === 'maintenance' ? 'text-red-600' : 'text-green-600'
 }`}>
 <span className={`w-2 h-2 rounded-full ${
 node.status === 'maintenance' ? 'bg-red-500' : 'bg-green-500'
 }`}></span>
 {node.description || busyStyle.label}
 </span>
 </div>
 )}
 
 {/* 使用率进度条 - 新线路不显示 */}
 <div className={`mb-3 ${fleetType === 'gpt-backup' ? 'mb-4' : ''} ${fleetType === 'claude-v2' ? 'hidden' : ''}`}>
 <div className="flex items-center justify-between text-xs text-ink-500 mb-1">
 <span>{t('aiFleet.busyLevel.usageRate')}</span>
 <span className={`font-medium ${
 node.status === 'maintenance' ? 'text-red-600' : 'text-green-600'
 }`}>{node.busyLevel}%</span>
 </div>
 <div className="w-full bg-cream-100 rounded-full h-2 overflow-hidden">
 <div
 className={`h-full transition-all duration-500 relative ${
 node.status === 'maintenance' ? 'bg-red-500' : 'bg-green-500'
 }`}
 style={{ width: `${node.busyLevel}%` }}
 >
 <div className="absolute inset-0 bg-white/20"></div>
 </div>
 </div>
 </div>
 
 {/* 立即使用按钮 - GPT车队使用更大的按钮 */}
 <button
 onClick={() => handleNodeClick(node, fleetType)}
 disabled={node.status === 'maintenance'}
 className={`w-full ${fleetType === 'gpt-backup' ? 'py-2.5' : 'py-1.5 sm:py-2'} rounded-md sm:rounded-lg font-medium text-xs sm:text-sm transition-all duration-300 ${
 node.status === 'maintenance'
 ? 'bg-cream-100 text-ink-400 cursor-not-allowed'
 : fleetType === 'gpt-backup'
 ? 'border-2 border-accent-300 text-accent-600 bg-white hover:bg-accent-50 hover:border-accent-400 hover:shadow-md hover:text-accent-700 font-bold'
 : 'border border-accent-300 text-accent-600 bg-white hover:bg-accent-50 hover:border-accent-400 hover:shadow-md hover:text-accent-700'
 }`}
 >
 {node.status === 'maintenance' ? t('aiFleet.nodeInfo.stopped') : t('aiFleet.nodeInfo.useNow')}
 </button>
 </div>
 </div>
 );
 })}
 </div>
 );
 };

 const runSpeedTest = async (node, fleetType) => {
   const ROUNDS = 3;
   const initLines = CLAUDE_LINES.map(l => ({ ...l, status: 'testing', latency: null, round: 0 }));
   setSpeedTestOverlay({ visible: true, lines: initLines, bestLine: null });
   const results = await Promise.all(
     CLAUDE_LINES.map(async (line) => {
       const latencies = [];
       for (let r = 0; r < ROUNDS; r++) {
         const start = performance.now();
         try {
           const ctrl = new AbortController();
           const t = setTimeout(() => ctrl.abort(), 5000);
           await fetch(`${CLAUDE_LINE_BASE_URLS[line.id]}/claude-auth-bridge.html`, {
             method: 'HEAD', mode: 'no-cors', cache: 'no-cache', signal: ctrl.signal,
           });
           clearTimeout(t);
           latencies.push(Math.round(performance.now() - start));
         } catch {}
         setSpeedTestOverlay(prev => ({
           ...prev,
           lines: prev.lines.map(l => l.id === line.id ? { ...l, round: r + 1 } : l),
         }));
       }
       const avg = latencies.length > 0
         ? Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length)
         : null;
       const status = avg !== null ? 'done' : 'failed';
       setSpeedTestOverlay(prev => ({
         ...prev,
         lines: prev.lines.map(l => l.id === line.id ? { ...l, status, latency: avg, round: ROUNDS } : l),
       }));
       return { ...line, latency: avg ?? Infinity };
     })
   );
   const ok = results.filter(r => r.latency !== Infinity);
   const best = ok.length > 0
     ? ok.reduce((a, b) => a.latency < b.latency ? a : b)
     : { ...CLAUDE_LINES[0], latency: null };
   setSpeedTestOverlay(prev => ({ ...prev, bestLine: best }));
   await new Promise(resolve => setTimeout(resolve, 1500));
   setSpeedTestOverlay({ visible: false, lines: [], bestLine: null });
   const carid = node.id;
   const uid = userInfo?.uid || userInfo?.id;
   const userToken = userInfo?.userToken;
   const bp = new URLSearchParams({ carid, uid, userToken });
   const bestBase = CLAUDE_LINE_BASE_URLS[best.id] || CLAUDE_LINE_BASE_URLS['us-direct'];
   const jumpUrl = `${bestBase}/claude-auth-bridge.html?${bp.toString()}`;
   window.open(jumpUrl, '_blank');
   const fleetName = fleets.find(f => f.type === fleetType)?.name || '克劳德';
   setManualRedirectModal({ visible: true, bridgeUrl: jumpUrl, fleetName });
 };

 const handleNodeClick = (node, fleetType) => {
 // 检查节点是否可用
 if (node.status === 'maintenance') {
 console.log('节点不可用:', node.id);
 return;
 }
 
 // 如果用户已登录，检查该车队是否过期
 if (isLoggedIn && userInfo) {
 let expiryDate = null;
 switch(fleetType) {
 case 'gpt-backup':
 expiryDate = (userInfo && userInfo.expiration_date);
 break;
 case 'claude':
 case 'claude-v2':
 expiryDate = (userInfo && userInfo.expiration_date_ye_claude);
 break;
 case 'gemini':
 expiryDate = (userInfo && userInfo.expiration_date_ye_gemini);
 break;
 case 'grok':
 expiryDate = (userInfo && userInfo.expiration_date_ye_grok);
 break;
 case 'sora':
 expiryDate = (userInfo && userInfo.expiration_date_ye_sora);
 break;
 default:
 expiryDate = (userInfo && userInfo.expiration_date);
 }
 
 // 如果有过期时间且已过期，显示弹窗
 if (expiryDate) {
 const expiry = new Date(expiryDate);
 const today = new Date();
 if (expiry < today) {
 const fleetName = fleets.find(f => f.type === fleetType)?.name || '该服务';
 setExpiredModal({
 visible: true,
 fleetName: fleetName,
 expiryDate: expiryDate
 });
 return;
 }
 }
 }
 
 // 打印用户信息和节点ID
 console.log('=== 节点点击事件 ===');
 console.log('用户信息:', userInfo);
 console.log('节点ID:', node.id);
 console.log('节点完整信息:', node);
 console.log('所属车队类型:', fleetType);
 console.log('===================');
 
 // 对所有支持的车队类型执行鉴权流程
 const supportedFleets = ['grok', 'sora', 'claude', 'claude-v2', 'gemini', 'gpt-backup'];
 
 // 如果是支持的车队类型
 if (supportedFleets.includes(fleetType)) {
 // 检查用户是否登录
 if (!isLoggedIn || !userInfo) {
 // 未登录用户，显示登录弹窗
 if (onLoginRequired) {
 onLoginRequired();
 } else {
 // 如果没有传入onLoginRequired函数，使用原有的提示逻辑
 const confirmLogin = window.confirm('使用AI服务需要先登录账号。\n\n点击"确定"前往登录页面，或点击"取消"继续浏览。');
 if (confirmLogin) {
 navigate('/');
 }
 }
 return;
 }
 
 // 已登录用户，继续原有的鉴权流程
 // 1. 获取三个信息
 const carid = node.id; // 车号ID从节点信息中获取
 const uid = userInfo.uid || userInfo.id; // 用户uid
 const userToken = userInfo.userToken; // 登录密码
 
 console.log(`${fleetType.toUpperCase()}鉴权信息:`, {
 carid,
 uid,
 userToken
 });
 
 // 检查必要信息是否完整
 if (!carid || !userToken) {
 alert('缺少必要的鉴权信息，请重新登录');
 return;
 }

 if (fleetType === 'claude' && lineMode === 'auto') {
 runSpeedTest(node, fleetType);
 return;
 }
 
 try {
 let jumpUrl;

 if (fleetType === 'claude-v2') {
 // 新线路：直接跳转，无需 bridge 页
 jumpUrl = `https://c2.aimonkey.plus/auth/login?userToken=${encodeURIComponent(userToken)}&carid=${encodeURIComponent(carid)}`;
 } else {
 // 其他车队走 bridge 页
 const bridgeParams = new URLSearchParams({ carid, uid, userToken });
 const lineForBridge = LINE_TAB_FLEETS.includes(fleetType) ? activeLine : 'us-direct';
 jumpUrl = `${getAuthBridgeUrl(fleetType, lineForBridge)}?${bridgeParams.toString()}`;
 }

 window.open(jumpUrl, '_blank');

 const fleetName = fleets.find(f => f.type === fleetType)?.name || `${fleetType.toUpperCase()} 服务`;
 setManualRedirectModal({
 visible: true,
 bridgeUrl: jumpUrl,
 fleetName: fleetName
 });
 } catch (error) {
 console.error('获取配置失败:', error);
 alert('配置错误，请联系管理员');
 }
 
 return;
 }
 
 // 其他车队的处理逻辑保持不变
 alert(`已选择 ${node.id}\n节点名称: ${node.name}\n状态: ${getStatusInfo(node.status).text}`);
 };

 return (
 <section className="pt-8 pb-8">
 <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

 {/* 车队网格 */}
 {loading ? (
 <LoadingSpinner />
 ) : error ? (
 <ErrorMessage />
 ) : fleets.length > 0 ? (
 <>
 {/* 车队Tab选择器 - 4个立即使用的卡片 - 移动端滚动，桌面 1×4 */}
 <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-6 sm:mb-8">
 {fleets.map((fleet, index) => {
 const aiStyle = getAIStyle(fleet.type);
 const IconComponent = aiStyle.icon;
 const isSelected = (selectedFleet && selectedFleet.id) === fleet.id;
 
 // 为每个车队类型固定配色方案
 const fleetColorMap = {
 'gpt': {
 third: 'from-blue-200 to-accent-300',
 second: 'from-green-200 to-teal-300'
 },
 'sora': {
 third: 'from-pink-200 to-rose-300',
 second: 'from-yellow-200 to-accent-300'
 },
 'claude': {
 third: 'from-accent-200 to-indigo-300',
 second: 'from-emerald-200 to-cyan-300'
 },
 'gemini': {
 third: 'from-green-200 to-emerald-300',
 second: 'from-teal-200 to-cyan-300'
 },
 'grok': {
 third: 'from-red-200 to-pink-300',
 second: 'from-blue-200 to-indigo-300'
 },
 'gpt-backup': {
 third: 'from-amber-200 to-accent-300',
 second: 'from-violet-200 to-accent-300'
 }
 };
 
 const colors = fleetColorMap[fleet.type] || fleetColorMap['gpt'];
 
 return (
 <div
 key={fleet.id}
 className={`bg-cream-50 rounded-xl overflow-hidden border transition-colors duration-200 group cursor-pointer ${
 isSelected ? 'border-accent-500 ring-1 ring-accent-500' : 'border-ink-200 hover:border-ink-900'
 }`}
 onClick={() => handleFleetClick(fleet)}
 onMouseEnter={(e) => handleMouseEnter(e, fleet)}
 onMouseMove={handleMouseMove}
 onMouseLeave={handleMouseLeave}
 >
 {/* 车队图标区域 - 正方形，选中时微染紫 */}
 <div className={`aspect-square relative overflow-hidden transition-colors duration-200 ${
 isSelected ? 'bg-accent-50' : 'bg-cream-100'
 }`}>
 <div className="relative h-full flex items-center justify-center p-3 sm:p-4">
 {/* 主车队图片容器 */}
 <div className="relative z-10 w-4/5 h-4/5">
 <div className={`w-full h-full rounded-lg overflow-hidden border transition-colors duration-200 ${
 isSelected ? 'border-accent-300' : 'border-ink-200'
 }`}>
 <img
 src={aiStyle.image}
 alt={fleet.name}
 className="w-full h-full object-cover"
 onError={(e) => {
 e.target.style.display = 'none';
 e.target.nextSibling.style.display = 'flex';
 }}
 />

 {/* 备用图标显示 */}
 <div
 className="w-full h-full bg-accent-500 rounded-lg flex items-center justify-center"
 style={{display: 'none'}}
 >
 <IconComponent className="w-12 h-12 text-white" />
 </div>
 </div>
 </div>
 </div>

 {/* 选中标记 —— 只在被选中时显示对号 */}
 {isSelected && (
 <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-accent-500 flex items-center justify-center z-20 shadow-sm">
 <CheckCircle className="w-4 h-4 text-white" strokeWidth={2.5} />
 </div>
 )}
 </div>

 {/* 车队信息 - 移动端隐藏名称 */}
 <div className="hidden sm:block p-3 sm:p-4">
 <h3 className={`text-xs sm:text-sm font-semibold leading-tight text-center ${
 isSelected ? 'text-accent-600' : 'text-ink-900 group-hover:text-accent-600'
 } ${fleetCardsConfig.fleetCards[fleet.type]?.subtitle ? 'mb-1' : 'mb-2 sm:mb-3'}`}>
 {fleet.name}
 </h3>
 {fleetCardsConfig.fleetCards[fleet.type]?.subtitle && (
 <p className="text-center text-xs text-ink-400 mb-2">
 {fleetCardsConfig.fleetCards[fleet.type].subtitle}
 </p>
 )}
 </div>
 </div>
 );
 })}
 </div>

 {/* Tooltip组件 */}
 {tooltip.visible && tooltip.fleetInfo && (
 <div
 className="fixed z-50 pointer-events-none"
 style={{
 left: `${tooltip.x}px`,
 top: `${tooltip.y}px`
 }}
 >
 <div className="bg-ink-900 text-white rounded-xl shadow-md p-4 min-w-[320px]">
 
 {/* Tooltip内容 */}
 <div className="space-y-3">
 {/* 标题 */}
 <div className="flex items-center gap-2 border-b border-ink-700 pb-2">
 <div className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center">
 {React.createElement(getAIStyle(tooltip.fleetInfo.type).icon, { className: "w-4 h-4 text-white" })}
 </div>
 <h4 className="font-bold text-sm">{tooltip.fleetInfo.name}</h4>
 </div>
 
 {/* 功能特点 */}
 <div>
 <p className="text-xs text-ink-400 mb-1">主要功能：</p>
 <ul className="text-xs space-y-1">
 {getFleetTooltipInfo(tooltip.fleetInfo.type).features.map((feature, idx) => (
 <li key={idx} className="flex items-start gap-1">
 <CheckCircle className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
 <span>{feature}</span>
 </li>
 ))}
 </ul>
 </div>
 
 {/* 价格信息 */}
 <div className="pt-2 border-t border-ink-700">
 <div>
 <p className="text-xs text-ink-400">价格</p>
 <p className="text-sm font-semibold text-accent-400">
 {getFleetTooltipInfo(tooltip.fleetInfo.type).price}
 </p>
 </div>
 </div>
 
 {/* 使用限制 - 支持多行显示 */}
 <div className="bg-ink-800 rounded-lg p-2">
 <p className="text-xs text-ink-400 mb-2">使用限制：</p>
 <div className="space-y-1">
 {getFleetTooltipInfo(tooltip.fleetInfo.type).limits.map((limit, idx) => (
 <div key={idx} className="flex items-start gap-1">
 <span className="text-yellow-400 text-xs mt-0.5">•</span>
 <p className="text-xs text-yellow-400">
 {limit}
 </p>
 </div>
 ))}
 </div>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* 账户状态栏 - 单行紧凑布局（Claude 风 + 状态指示） */}
 {isLoggedIn && userInfo && selectedFleet && (() => {
 // 根据车队类型取到期时间
 const rawExpiry = (() => {
 switch(selectedFleet.type) {
 case 'gpt-backup': return userInfo.expiration_date;
 case 'claude':
 case 'claude-v2': return userInfo.expiration_date_ye_claude;
 case 'gemini': return userInfo.expiration_date_ye_gemini;
 case 'grok': return userInfo.expiration_date_ye_grok;
 case 'sora': return userInfo.expiration_date_ye_sora;
 default: return userInfo.expiration_date;
 }
 })();
 const expiryStr = rawExpiry
 ? new Date(rawExpiry).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-')
 : '未开通';
 const diffDays = rawExpiry
 ? Math.ceil((new Date(rawExpiry) - new Date()) / (1000 * 60 * 60 * 24))
 : null;
 const progressWidth = diffDays === null ? '0%'
 : diffDays > 90 ? '100%'
 : diffDays > 0 ? `${(diffDays / 90) * 100}%`
 : '0%';
 const uidFull = userInfo.uid || userInfo.id || userInfo.email || '用户';
 const uid = uidFull.length > 16
 ? `${uidFull.substring(0, 6)}...${uidFull.substring(uidFull.length - 6)}`
 : uidFull;
 const AiIcon = getAIStyle(selectedFleet.type).icon;

 // 状态配色 —— 按紧急度分级
 const urgency =
 diffDays === null ? 'none'
 : diffDays <= 0 ? 'expired'
 : diffDays <= 7 ? 'urgent'
 : diffDays <= 30 ? 'warning'
 : 'healthy';
 const statusConfig = {
 healthy: { dot: 'bg-green-500', daysColor: 'text-accent-600', progressColor: 'bg-accent-500', label: '服务正常' },
 warning: { dot: 'bg-amber-500', daysColor: 'text-amber-700', progressColor: 'bg-amber-500', label: '即将到期' },
 urgent: { dot: 'bg-red-500', daysColor: 'text-red-600', progressColor: 'bg-red-500', label: '紧急续费' },
 expired: { dot: 'bg-ink-400', daysColor: 'text-ink-500', progressColor: 'bg-ink-300', label: '已过期' },
 none: { dot: 'bg-ink-300', daysColor: 'text-ink-500', progressColor: 'bg-ink-200', label: '未开通' },
 }[urgency];

 return (
 <div className="bg-cream-100 border border-ink-200 rounded-xl mb-4 sm:mb-8 px-4 sm:px-5 py-3 flex items-center gap-x-4 gap-y-2 flex-wrap">
 {/* 左区：状态点 + Logo + 车队名（衬线） */}
 <div className="flex items-center gap-2.5 flex-shrink-0">
 {/* 状态指示点 —— 脉冲动画给健康状态 */}
 <span className="relative flex-shrink-0 w-2 h-2" title={statusConfig.label}>
 {urgency === 'healthy' && (
 <span className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-40"></span>
 )}
 <span className={`relative block w-2 h-2 rounded-full ${statusConfig.dot}`}></span>
 </span>
 {/* Logo */}
 <div className="w-9 h-9 rounded-lg overflow-hidden bg-cream-50 border border-ink-200 p-0.5">
 <img
 src={getAIStyle(selectedFleet.type).image}
 alt={selectedFleet.name}
 className="w-full h-full rounded-md object-cover"
 onError={(e) => {
 e.target.style.display = 'none';
 e.target.nextSibling.style.display = 'flex';
 }}
 />
 <div
 className="w-full h-full bg-accent-500 rounded-md flex items-center justify-center"
 style={{display: 'none'}}
 >
 <AiIcon className="w-4 h-4 text-white" />
 </div>
 </div>
 {/* 车队名 —— 衬线体 */}
 <h3 className="font-serif text-xl text-ink-900 tracking-tight whitespace-nowrap leading-none">
 {selectedFleet.name}
 </h3>
 </div>

 {/* 竖线分隔 */}
 <span className="hidden sm:block w-px h-6 bg-ink-200 flex-shrink-0"></span>

 {/* 账号 */}
 <div className="flex items-baseline gap-1.5 text-sm whitespace-nowrap">
 <span className="text-ink-500 text-xs">账号</span>
 <span className="text-ink-900 font-medium tabular-nums" title={uidFull}>{uid}</span>
 </div>

 {/* 竖线分隔 */}
 <span className="hidden sm:block w-px h-6 bg-ink-200 flex-shrink-0"></span>

 {/* 会员至 */}
 <div className="flex items-baseline gap-1.5 text-sm whitespace-nowrap">
 <span className="text-ink-500 text-xs">会员至</span>
 <span className="text-ink-900 font-medium tabular-nums">{expiryStr}</span>
 </div>

 {/* 竖线分隔 */}
 <span className="hidden sm:block w-px h-6 bg-ink-200 flex-shrink-0"></span>

 {/* 剩余天数 + 进度条（紧急度配色） */}
 <div className="flex items-center gap-2 whitespace-nowrap">
 <span className={`text-sm font-semibold ${statusConfig.daysColor} tabular-nums`}>
 {diffDays === null ? '未开通'
 : diffDays > 0 ? <>剩余 {diffDays} 天</>
 : diffDays === 0 ? '今天到期'
 : '已过期'}
 </span>
 <div className="w-16 h-1.5 bg-ink-100 rounded-full overflow-hidden">
 <div
 className={`h-full ${statusConfig.progressColor} rounded-full transition-all duration-500`}
 style={{ width: progressWidth }}
 />
 </div>
 </div>

 {/* 充值升级按钮 —— 推到最右，带箭头 */}
 <button
 onClick={() => navigate('/purchase')}
 className="sm:ml-auto inline-flex items-center justify-center gap-1.5 bg-accent-500 text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap group"
 >
 <Crown className="w-4 h-4" />
 <span>充值升级</span>
 <span className="group-hover:translate-x-0.5 transition-transform duration-200">→</span>
 </button>
 </div>
 );
 })()}

 {/* 公告栏 - 左侧紫竖条；主文案由后端按 type 下发（同 type 可多条），与登录态解耦，未选车队时按 gpt 显示 */}
 {(() => {
 const tipKey = resolveWebInfoKey(selectedFleet?.type);
 const tips =
 webInfoTips[tipKey]
 || webInfoTips['gpt']
 || ['进入新节点后，请开启新对话才能使用该账号！'];
 const firstTip = selectedFleet
 ? fleetCardsConfig.fleetCards[selectedFleet.type]?.importantTips?.[0]
 : null;
 const multi = tips.length > 1;
 return (
 <div className="mb-4 sm:mb-6 bg-accent-50/60 border-l-4 border-accent-500 rounded-r-xl px-4 py-3 flex items-start gap-3">
 <Info className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
 <div className="text-sm leading-relaxed flex-1 min-w-0">
 {tips.map((tip, idx) => (
 <p key={idx} className="font-bold text-ink-900">
 {multi ? `${idx + 1}. ` : ''}{tip}
 </p>
 ))}
 {firstTip ? <p className="text-ink-600 mt-1">· {firstTip}</p> : null}
 </div>
 </div>
 );
 })()}

 {/* 选中车队的节点列表 */}
 {selectedFleet && (
 <div className="bg-cream-50 rounded-xl p-4 sm:p-6 border border-ink-200 relative min-h-[300px]">
 {/* Loading 骨架屏（Claude 风脉冲方块） */}
 {loadingNodes && (
 <div className="absolute inset-0 z-20 bg-cream-50/95 rounded-xl px-4 sm:px-6 pt-20 pb-6">
 <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
 {Array.from({ length: 10 }).map((_, i) => (
 <div
 key={i}
 className="rounded-xl border border-ink-200 bg-cream-100 h-48 animate-pulse overflow-hidden flex flex-col"
 style={{ animationDelay: `${i * 60}ms` }}
 >
 <div className="h-16 bg-cream-200/60 border-b border-ink-200"></div>
 <div className="flex-1 p-3 space-y-2">
 <div className="h-3 w-3/4 rounded bg-ink-100"></div>
 <div className="h-3 w-1/2 rounded bg-ink-100"></div>
 <div className="mt-auto h-8 rounded bg-ink-100"></div>
 </div>
 </div>
 ))}
 </div>
 <div className="absolute left-1/2 top-6 -translate-x-1/2 flex items-center gap-2 text-sm text-ink-500">
 <span className="relative flex w-2 h-2">
 <span className="absolute inset-0 rounded-full bg-accent-500 animate-ping opacity-60"></span>
 <span className="relative block w-2 h-2 rounded-full bg-accent-500"></span>
 </span>
 <span>{t('aiFleet.loadingNodes')}</span>
 </div>
 </div>
 )}
 
 {/* Tab切换栏 - 单行 */}
 <div className="mb-6 flex items-center gap-2 flex-wrap">
 {/* 车队图标 */}
 <div className="w-8 h-8 rounded-lg overflow-hidden shadow-sm flex-shrink-0">
 <img src={getAIStyle(selectedFleet.type).image} alt={selectedFleet.name}
 className="w-full h-full object-cover"
 onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
 <div className={`w-full h-full bg-gradient-to-br ${getAIStyle(selectedFleet.type).gradient} rounded flex items-center justify-center`} style={{display:'none'}}>
 {React.createElement(getAIStyle(selectedFleet.type).icon, { className: "w-4 h-4 text-white" })}
 </div>
 </div>

 {/* 线路切换 */}
 {selectedFleet.type === 'claude' ? (
   <>
     <div className="inline-flex items-center gap-1 p-0.5 rounded-lg bg-cream-100 border border-ink-200">
       <button onClick={() => setLineMode('auto')}
         className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-200 ${lineMode === 'auto' ? 'bg-accent-500 text-white shadow-sm' : 'text-ink-600 hover:text-ink-900'}`}>
         <Activity className="w-3 h-3" />
         <span>自动测速</span>
       </button>
       <button onClick={() => setLineMode('manual')}
         className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-200 ${lineMode === 'manual' ? 'bg-accent-500 text-white shadow-sm' : 'text-ink-600 hover:text-ink-900'}`}>
         <Globe className="w-3 h-3" />
         <span>手动选择</span>
       </button>
     </div>
     {lineMode === 'manual' && (
       <div className="inline-flex items-center gap-1 p-0.5 rounded-lg bg-cream-100 border border-ink-200">
         {CLAUDE_LINES.map((line) => {
           const isActive = activeLine === line.id;
           return (
             <button key={line.id} onClick={() => setActiveLine(line.id)}
               className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all duration-200 ${isActive ? 'bg-accent-500 text-white shadow-sm' : 'text-ink-600 hover:text-ink-900'}`}>
               <span className={`w-1.5 h-1.5 rounded-full ${line.dotColor}`}></span>
               <span>{line.name}</span>
             </button>
           );
         })}
       </div>
     )}
   </>
 ) : LINE_TAB_FLEETS.includes(selectedFleet.type) ? (
   <div className="inline-flex items-center gap-1 p-0.5 rounded-lg bg-cream-100 border border-ink-200">
     {lineOptions.map((line) => {
       const isActive = activeLine === line.id;
       return (
         <button key={line.id} onClick={() => setActiveLine(line.id)}
           className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-200 ${isActive ? 'bg-accent-500 text-white shadow-sm' : 'text-ink-600 hover:text-ink-900'}`}>
           <span className={`w-1.5 h-1.5 rounded-full ${line.dotColor}`}></span>
           <span>{line.name}</span>
           <span className={`hidden md:inline ${isActive ? 'text-white/70' : 'text-ink-400'}`}>· {line.subtitle}</span>
         </button>
       );
     })}
   </div>
 ) : (
   <h4 className="text-sm font-bold text-ink-900">{t('aiFleet.selfSelectNumber')}</h4>
 )}

 {/* 刷新 */}
 <button
   onClick={() => { setNodeData(prev => ({ ...prev, [selectedFleet.id]: undefined })); setLoadingNodes(true); fetchNodeData(selectedFleet, 0); }}
   disabled={loadingNodes}
   className="inline-flex items-center gap-1 px-2 py-1 text-xs text-accent-600 border border-accent-200 rounded-lg hover:bg-accent-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
 >
   <RefreshCw className={`w-3 h-3 ${loadingNodes ? 'animate-spin' : ''}`} />
   {t('aiFleet.refresh')}
 </button>

 {/* 分隔线 */}
 <span className="w-px h-4 bg-ink-200 flex-shrink-0 hidden sm:block"></span>

 {/* 筛选标签 - 内联 */}
 {(() => {
 const fleetType = selectedFleet.type;
 const enabledCategories = defaultFilterConfig.fleetCategories[fleetType] || [];
 const fleetTags = fleetFilterTags[fleetType];

 if (!fleetTags || enabledCategories.length === 0) return null;

 const countBaseNodes = nodeData[selectedFleet.id] || [];

 return enabledCategories.flatMap(category => {
 if (!fleetTags[category]) return [];

 return fleetTags[category].map(tag => {
 const isActive = activeFilters[category] === tag.id;
 const colorStyles = getTagColorStyles(tag.color);

 return (
 <button
 key={`${category}-${tag.id}`}
 onClick={() => handleFilterClick(category, tag.id)}
 className={`
 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium
 transition-all duration-200 border
 ${isActive
 ? `${colorStyles.activeBg} ${colorStyles.activeText}`
 : `${colorStyles.bg} ${colorStyles.text} hover:shadow-sm`
 }
 `}
 >
 <span>{t(tag.nameKey, tag.name)}</span>
 <span className={`
 px-1.5 py-0.5 rounded-full text-xs font-bold
 ${isActive ? 'bg-white/20' : 'bg-ink-100 text-ink-600'}
 `}>
 {countBaseNodes.filter(tag.filter).length}
 </span>
 </button>
 );
 });
 });
 })()}

 {/* 分隔线 */}
 <span className="w-px h-4 bg-ink-200 flex-shrink-0 hidden sm:block"></span>

 {/* 状态指示灯 */}
 <span className="flex items-center gap-1 text-xs text-ink-500"><span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>{t('aiFleet.busyLevel.idle')}</span>
 <span className="flex items-center gap-1 text-xs text-ink-500"><span className="w-1.5 h-1.5 bg-yellow-500 rounded-full"></span>{t('aiFleet.busyLevel.moderate')}</span>
 <span className="flex items-center gap-1 text-xs text-ink-500"><span className="w-1.5 h-1.5 bg-red-500 rounded-full"></span>{t('aiFleet.busyLevel.busy')}</span>
 </div>

{/* 节点列表 */}
 <NodeGrid
 nodes={filteredNodes}
 fleetType={selectedFleet.type}
 />
 </div>
 )}
 </>
 ) : (
 <EmptyState />
 )}

 </div>
 
 {/* 过期提示弹窗 */}
 {expiredModal.visible && (
 <div className="fixed inset-0 bg-ink-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
 <div className="bg-cream-50 rounded-xl shadow-md max-w-md w-full p-8 animate-fade-in-scale border border-ink-200 relative">

 {/* 关闭按钮 */}
 <button
 onClick={() => setExpiredModal({ visible: false, fleetName: '', expiryDate: null })}
 className="absolute top-5 right-5 p-2 rounded-lg hover:bg-cream-100 transition-colors duration-200 z-10"
 >
 <X className="w-5 h-5 text-ink-600" />
 </button>

 {/* Logo */}
 <div className="w-20 h-20 rounded-xl overflow-hidden mx-auto mb-6 bg-accent-500 p-1 border border-ink-200">
 <div className="w-full h-full rounded-lg overflow-hidden bg-cream-50">
 <img
 src="/images/logo192.png"
 alt="紫刀AI Logo"
 className="w-full h-full object-cover"
 onError={(e) => {
 e.target.style.display = 'none';
 e.target.nextSibling.style.display = 'flex';
 }}
 />
 <div
 className="w-full h-full bg-accent-500 flex items-center justify-center"
 style={{display: 'none'}}
 >
 <Bot className="w-10 h-10 text-white" />
 </div>
 </div>
 </div>

 {/* 标题 */}
 <h3 className="font-serif text-3xl text-ink-900 text-center mb-2 tracking-tight">
 {t('aiFleet.expired.title')}
 </h3>

 {/* 副标题 */}
 <p className="text-center text-ink-500 text-sm mb-6">
 {t('aiFleet.expired.subtitle')}
 </p>

 {/* 账户信息卡片 */}
 <div className="bg-cream-100 rounded-lg p-4 mb-4 border border-ink-200">
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 rounded-lg bg-accent-500 flex items-center justify-center">
 <User className="w-5 h-5 text-white" />
 </div>
 <div className="flex-1">
 <p className="text-xs text-ink-500 font-medium uppercase tracking-wider">{t('aiFleet.expired.currentAccount')}</p>
 <p className="text-sm font-semibold text-ink-900 mt-0.5" title={userInfo?.uid || userInfo?.id || userInfo?.email || '用户'}>
 {(() => {
 const uid = userInfo?.uid || userInfo?.id || userInfo?.email || '用户';
 if (uid.length > 20) {
 const start = uid.substring(0, 8);
 const end = uid.substring(uid.length - 8);
 return `${start}...${end}`;
 }
 return uid;
 })()}
 </p>
 </div>
 </div>
 </div>

 {/* 服务信息 */}
 <div className="bg-cream-100 rounded-lg p-4 mb-6 border border-ink-200">
 <div className="flex items-center justify-between mb-2">
 <span className="text-xs font-medium text-ink-600 uppercase tracking-wider">{t('aiFleet.expired.expiredService')}</span>
 <span className="px-2.5 py-0.5 bg-accent-500 text-white text-xs font-medium rounded-full">{t('aiFleet.expired.expiredLabel')}</span>
 </div>
 <p className="text-base font-semibold text-ink-900 mb-2">
 {expiredModal.fleetName}
 </p>
 <div className="flex items-center gap-2 text-sm text-ink-600">
 <Calendar className="w-4 h-4" />
 <span>{t('aiFleet.expired.expiryTime')}{expiredModal.expiryDate && new Date(expiredModal.expiryDate).toLocaleDateString('zh-CN', {
 year: 'numeric',
 month: '2-digit',
 day: '2-digit'
 })}</span>
 </div>
 </div>

 {/* 续费按钮 */}
 <button
 onClick={() => {
 setExpiredModal({ visible: false, fleetName: '', expiryDate: null });
 navigate('/shop?type=web');
 }}
 className="w-full inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
 >
 <Crown className="w-5 h-5" />
 <span className="text-base">{t('aiFleet.expired.renewNow')}</span>
 </button>

 {/* 底部提示文字 */}
 <p className="text-center text-xs text-ink-400 mt-4">
 {t('aiFleet.expired.renewToRestore')}
 </p>
 </div>
 </div>
 )}

 {/* 手动跳转弹窗 */}
 {manualRedirectModal.visible && (
 <div className="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
 <div className="bg-cream-50 rounded-xl shadow-md max-w-md w-full p-6 border border-ink-200 relative">
 {/* 关闭按钮 */}
 <button
 onClick={() => setManualRedirectModal({ visible: false, bridgeUrl: '', fleetName: '' })}
 className="absolute top-4 right-4 w-8 h-8 rounded-lg hover:bg-cream-100 flex items-center justify-center transition-colors duration-200"
 >
 <X className="w-4 h-4 text-ink-600" />
 </button>

 <div className="text-center">
 <div className="w-14 h-14 bg-accent-100 border border-accent-200 rounded-full flex items-center justify-center mx-auto mb-4">
 <Bot className="w-7 h-7 text-accent-600" />
 </div>

 <h3 className="font-serif text-2xl text-ink-900 mb-2 tracking-tight">
 {t('aiFleet.manualRedirect.ready')}
 </h3>

 <p className="text-ink-600 text-sm mb-4">
 {t('aiFleet.manualRedirect.description')} <strong className="text-ink-900">{manualRedirectModal.fleetName}</strong>
 </p>

 <p className="text-ink-500 text-xs mb-6">
 {t('aiFleet.manualRedirect.instruction')}
 </p>

 <button
 onClick={() => window.location.href = manualRedirectModal.bridgeUrl}
 className="w-full inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 text-white py-3 rounded-lg font-medium transition-colors duration-200"
 >
 <Bot className="w-5 h-5" />
 {t('aiFleet.manualRedirect.openManually')}
 </button>
 </div>
 </div>
 </div>
 )}

 {speedTestOverlay.visible && (
   <div className="fixed inset-0 bg-ink-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
     <div className="bg-cream-50 rounded-2xl shadow-xl max-w-sm w-full p-6 border border-ink-200">
       <div className="text-center mb-5">
         <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-3">
           <Activity className="w-6 h-6 text-accent-600 animate-pulse" />
         </div>
         <h3 className="text-base font-bold text-ink-900">正在为您选择最快线路</h3>
         <p className="text-xs text-ink-500 mt-1">4 条线路各测 3 次取均值，自动跳转最稳定的</p>
       </div>
       <div className="space-y-2.5">
         {speedTestOverlay.lines.map(line => {
           const isBest = speedTestOverlay.bestLine?.id === line.id;
           return (
             <div key={line.id} className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all duration-300 ${isBest ? 'border-green-400 bg-green-50' : 'border-ink-200 bg-cream-100'}`}>
               <span className={`w-2 h-2 rounded-full flex-shrink-0 ${line.dotColor}`}></span>
               <span className="flex-1 text-sm font-medium text-ink-800">{line.name}</span>
               {line.status === 'testing' ? (
                 <span className="flex items-center gap-1 text-xs text-ink-400">
                   <Loader2 className="w-3 h-3 animate-spin" />
                   {line.round > 0 ? `${line.round}/3` : '测速中'}
                 </span>
               ) : line.status === 'failed' ? (
                 <span className="text-xs text-red-500 font-medium">超时</span>
               ) : (
                 <span className={`text-xs font-bold ${isBest ? 'text-green-600' : 'text-ink-500'}`}>
                   {line.latency}ms 均值{isBest ? ' ⚡最快' : ''}
                 </span>
               )}
             </div>
           );
         })}
       </div>
       <div className="mt-5 text-center">
         {speedTestOverlay.bestLine ? (
           <>
             <p className="text-sm font-semibold text-green-600">已选：{speedTestOverlay.bestLine.name}</p>
             <p className="text-xs text-ink-400 mt-1">正在跳转...</p>
           </>
         ) : (
           <p className="text-xs text-ink-400">测速完成后自动跳转</p>
         )}
       </div>
     </div>
   </div>
 )}

 </section>
 );
}
